import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { MIN_VIEW_HEIGHT } from '../constants';

/**
 * Main card wrapper — matches other community-stats cards
 */
export const StyledCard = styled(Stack)(({ theme }) => ({
  flex: 1,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.common.black
      : theme.palette.common.white,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.customShape.borderRadiusXLarge,
  padding: `${theme.spacing(4)} ${theme.spacing(6)}`,
  gap: theme.spacing(4),

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4),
  },
}));

/**
 * Header row: title group on left, toggle on right
 */
export const StyledHeaderContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
}));

/**
 * Title + info icon row — expected size 122×24 in design
 */
export const StyledTitleContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: 122,
  height: 24,
}));

export const StyledTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(16),
  fontWeight: 500,
  lineHeight: 1.5,
  letterSpacing: '0.08px',
  color: theme.palette.text.primary,
}));

export const StyledInfoButton = styled(IconButton)({
  padding: 0,
});

/**
 * Graph | List segmented toggle container
 */
export const StyledToggleGroup = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
});

interface ToggleButtonProps {
  active: boolean;
  position: 'left' | 'right';
}

export const StyledViewToggleButton = styled(Button)<ToggleButtonProps>(
  ({ theme, active, position }) => ({
    height: 32,
    minWidth: 0,
    padding: theme.spacing(0, 4),
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.07px',
    textTransform: 'none',
    color: active ? theme.palette.text.primary : theme.palette.text.secondary,
    backgroundColor: active ? 'rgba(170, 178, 255, 0.24)' : 'transparent',
    border: `1px solid ${theme.palette.info.main}`,
    borderRadius: 0,
    borderLeft: position === 'right' ? 'none' : undefined,
    borderTopLeftRadius: position === 'left' ? 30 : 0,
    borderBottomLeftRadius: position === 'left' ? 30 : 0,
    borderTopRightRadius: position === 'right' ? 30 : 0,
    borderBottomRightRadius: position === 'right' ? 30 : 0,

    '&:hover': {
      backgroundColor: active
        ? 'rgba(170, 178, 255, 0.35)'
        : theme.palette.action.hover,
    },
  }),
);

/**
 * Thin divider line between sections — marginTop: auto pushes it (and the
 * educational section below it) to the bottom of the card so both cards'
 * dividers stay aligned regardless of content height differences.
 */
export const StyledDivider = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '1px',
  backgroundColor: theme.palette.divider,
  marginTop: 'auto',
}));

/**
 * Best bid / ask row
 */
export const StyledBidAskRow = styled(Stack)({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
});

export const StyledBidAskGroup = styled(Stack)({
  flexDirection: 'column',
  gap: 0,
});

interface BidAskGroupProps {
  align?: 'left' | 'right';
}

export const StyledBidAskGroupAligned = styled(Stack)<BidAskGroupProps>(
  ({ align = 'left' }) => ({
    flexDirection: 'column',
    alignItems: align === 'right' ? 'flex-end' : 'flex-start',
  }),
);

export const StyledBidAskLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(16),
  fontWeight: 400,
  lineHeight: 1.4,
  letterSpacing: '0.08px',
  color: theme.palette.text.second,
}));

export const StyledBidAskPrice = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(36),
  fontWeight: 700,
  fontStyle: 'italic',
  lineHeight: 1,
  letterSpacing: '0.09px',
  color: theme.palette.text.primary,
}));

export const StyledBidAskQuantity = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(16),
  fontWeight: 400,
  lineHeight: 1.4,
  letterSpacing: '0.08px',
  color: theme.palette.text.second,
}));

/**
 * Graph/list view container — prevents layout jump when toggling
 */
export const StyledViewContainer = styled(Box)(() => ({
  position: 'relative',
  overflow: 'hidden',
  minHeight: MIN_VIEW_HEIGHT,
  maxHeight: MIN_VIEW_HEIGHT,
  overflowY: 'auto',
}));

interface FadeWrapperProps {
  isPositioned?: boolean;
}

/**
 * Wrapper for Fade content — absolute when inactive so only active view affects layout
 */
export const StyledFadeWrapper = styled(Box)<FadeWrapperProps>(
  ({ isPositioned = false }) => ({
    position: isPositioned ? 'absolute' : 'relative',
    top: 0,
    left: 0,
    right: 0,
  }),
);

/**
 * Chart area container — dot patterns are rendered via SVG inside the chart
 */
export const StyledChartContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  borderRadius: theme.customShape.borderRadiusXLarge,
}));

/**
 * Tooltip overlay for chart hover
 */
export const StyledTooltip = styled(Box)(({ theme }) => ({
  position: 'absolute',
  pointerEvents: 'none',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(0, 0, 0, 0.85)'
      : 'rgba(255, 255, 255, 0.95)',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 2),
  transform: 'translateX(-50%)',
  whiteSpace: 'nowrap',
  zIndex: 10,
}));

/**
 * Custom x-axis labels row with evenly spaced price labels (below dotted chart area)
 */
export const StyledXAxisLabels = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  width: '100%',
  paddingLeft: 0,
  paddingRight: 0,
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(1),
}));

/**
 * List view container — two-column bid/ask table. Height is dynamic based on row count.
 * minHeight matches chart area to prevent layout jump when toggling views.
 */
export const StyledListContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing(6),
  padding: theme.spacing(1, 4),
  overflow: 'hidden',
  minHeight: MIN_VIEW_HEIGHT,
}));

export const StyledListColumn = styled(Stack)(({ theme }) => ({
  flex: 1,
  flexDirection: 'column',
  gap: theme.spacing(4),
  minWidth: 0,
}));

export const StyledVerticalDivider = styled(Box)(({ theme }) => ({
  width: 1,
  alignSelf: 'stretch',
  backgroundColor: theme.palette.divider,
  flexShrink: 0,
}));

export const StyledListHeaderRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontSize: theme.typography.pxToRem(14),
  letterSpacing: '0.07px',
}));

export const StyledListRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontSize: theme.typography.pxToRem(16),
  letterSpacing: '0.08px',
}));

interface ListHeaderLabelProps {
  labelVariant?: 'size' | 'bid' | 'ask';
}

export const StyledListHeaderLabel = styled(Typography)<ListHeaderLabelProps>(
  ({ theme, labelVariant = 'size' }) => ({
    fontSize: theme.typography.pxToRem(14),
    fontWeight: labelVariant === 'size' ? 400 : 600,
    lineHeight: 1.5,
    letterSpacing: '0.07px',
    color:
      labelVariant === 'bid'
        ? theme.palette.success.main
        : labelVariant === 'ask'
          ? theme.palette.error.main
          : theme.palette.text.third,
  }),
);

export const StyledListPrice = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(16),
  fontWeight: 500,
  lineHeight: 1.4,
  letterSpacing: '0.08px',
  color: theme.palette.text.second,
}));

export const StyledListSize = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(16),
  fontWeight: 400,
  lineHeight: 1.4,
  letterSpacing: '0.08px',
  color: theme.palette.text.second,
}));

/**
 * Loading / no-data container
 */
export const StyledLoadingContainer = styled(Stack)(({ theme }) => ({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: theme.spacing(75),
}));
